"use client";

import type { ReactNode } from "react";
import { Alert } from "../alert";
import { Knopf } from "../knopf";
import { Hinweissatz } from "../formular/rueckmeldung";
import { EditorLeiste, type EditorLeistenBefehle, type EditorLeistenZustand } from "./editor-leiste";
import { EditorEinfuegenMenue, type EditorEinfuegeBefehle } from "./bearbeiten/einfuegen-menue";
import { EditorSpeicherstandAnzeige, SPEICHERN_FEHLSCHLAG_SATZ, type EditorSpeicherstand } from "./editor-speicherstand";
import type { EditorFlaechenHoehe } from "./editor-stile";

// Rahmen: Karte mit Rand und Fokusring (Preline-Formularfeld: focus:border-primary-focus focus:ring-primary-focus).
const RAHMEN_KLASSE =
  "overflow-hidden rounded-xl border border-layer-line bg-layer shadow-2xs focus-within:border-primary-focus focus-within:ring-1 focus-within:ring-primary-focus";
const RAHMEN_LESEN_KLASSE = "overflow-hidden rounded-xl border border-layer-line bg-layer";
const RAHMEN_LESEN_OHNE_KLASSE = "overflow-hidden";
// Kopf bricht um: reicht die Breite nicht, rutscht der Speicherstand in eine eigene Zeile, statt die Knopfreihe zu quetschen.
const KOPF_KLASSE = "flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-layer-line p-1";
const STAND_KOPF_KLASSE = "hidden shrink-0 pe-2 sm:flex";
// Mit Menü „Einfügen“: die Leiste bricht um, das Menü bleibt daneben stehen (mobil immer erreichbar).
const LEISTE_MIT_MENUE_KLASSE = "flex min-w-0 max-w-full items-center gap-x-1";
const MENUE_KLASSE = "shrink-0 ps-1 border-s border-layer-line";
const STAND_UNTEN_KLASSE = "mt-2 sm:hidden";
const FLAECHE_KLASSE = "relative";
const PLATZHALTER_KLASSE = "pointer-events-none absolute top-3 sm:top-4 inset-s-3 sm:inset-s-4 text-sm text-muted-foreground-1";
const KONFLIKT_KLASSE = "border-b border-layer-line p-3";
const KONFLIKT_KNOEPFE_KLASSE = "mt-3 flex flex-wrap items-center gap-2";
const SKELETT_KOPF_KLASSE = "flex items-center gap-x-1 border-b border-layer-line p-1";
const SKELETT_KNOPF_KLASSE = "size-8.5 rounded-lg bg-muted motion-safe:animate-pulse";
const SKELETT_FLAECHE_KLASSE = "min-h-56 space-y-3 p-3 sm:p-4";
const SKELETT_FLAECHE_KOMPAKT_KLASSE = "min-h-24 space-y-3 p-3 sm:p-4";
const SKELETT_FLAECHE_MITWACHSEND_KLASSE = "space-y-3 p-3 sm:p-4";
const SKELETT_FLAECHE_BUENDIG_KLASSE = "space-y-3";
const SKELETT_ZEILE_KLASSE = "h-4 rounded bg-muted motion-safe:animate-pulse";
const SKELETT_ZEILE_KURZ_KLASSE = "h-4 w-2/3 rounded bg-muted motion-safe:animate-pulse";

/**
 * Konflikt-Hinweis im Rahmen (Paket P4-S Slice S9, O13; additiv): der Text wurde inzwischen anderswo geändert. Der Baustein legt nur
 * aus: Satz und beide Knopf-Beschriftungen kommen von der Seite (Wortquelle), ebenso die Rückfragen vor dem Verwerfen bzw. Überschreiben
 * (D6b). Gezeigt wird er nur, wenn der Speicherstand `konflikt` ist.
 */
export interface EditorKonflikt {
  /** Der Hinweissatz (r105, ohne Code). */
  satz: string;
  /** Beschriftung „Neuere Fassung laden“ (verwirft die eigenen ungespeicherten Eingaben). */
  neuereText: string;
  /** Beschriftung „Meine Fassung speichern“ (überschreibt bewusst). */
  meineText: string;
  beiNeuere: () => void;
  beiMeine: () => void;
  /**
   * Abweichung AW-067 (1). Dritter, ruhiger Weg (Wiki P-W4a, Bild `w5-c-konflikt-rahmen`, additiv): „Unterschiede ansehen“ als Textknopf neben den zwei
   * Knöpfen. Der Baustein öffnet nichts selbst; die Seite zeigt die Unterschiede (Modal) und bleibt für den Satz und die Daten
   * zuständig. Ohne die Angabe bleibt der Konflikt-Hinweis unverändert (Besprechungen).
   */
  unterschiede?: { text: string; onClick: () => void };
}

/**
 * Rahmen der Editor-Einfassung (G5 Variante A, Bauplan E2): Karte nach dem Preline-Block `rich-text-editor-card`
 * mit Leiste oben, der Inhaltsfläche und dem Speicherstand (rechts in der Leiste, mobil darunter).
 *
 * Zustände (Design-Guide `editor-einfassung.md`): `laedt` = Skelett mit gleicher Mindesthöhe (kein Layoutsprung, der
 * Editor rendert erst nach dem Einhängen), `aria-busy`; `readOnly` = Rahmen ohne Leiste und Speicherstand (Lesen);
 * leer = Platzhalter; Fokus = Fokusring am Rahmen; Fehlschlag Speichern = Hinweissatz (r105), Eingaben bleiben.
 * Der Baustein hält keinen Editor: `children` ist die Inhaltsfläche (`HornEditorContent` aus `@horn/editor`), `leiste` kommt
 * aus `useHornLeiste`. Er prüft keine Rechte und speichert nichts.
 */
export function EditorRahmen({
  leiste,
  laedt = false,
  readOnly = false,
  platzhalter,
  speicherstand,
  konflikt,
  fehlschlagSatz = SPEICHERN_FEHLSCHLAG_SATZ,
  label = "Textformatierung",
  einfuegen,
  hoehe = "standard",
  rahmenlos = false,
  children,
}: {
  /**
   * Nur lesen, ohne eigenen Rahmen (additiv, Besprechung-Seite neu, Slice 4, Betreiber 19.10.2026: „veröffentlichtes Protokoll ohne Rahmen im Rahmen“): der Text sitzt in
   * einer Karte, die schon einen Rahmen trägt. Gilt nur zusammen mit `readOnly`; ein bearbeitbarer Editor behält seinen Rahmen.
   */
  rahmenlos?: boolean;
  /** Zustand und Befehle der Leiste (`useHornLeiste`); `null` solange der Editor nicht eingehängt ist. */
  leiste: { zustand: EditorLeistenZustand; befehle: EditorLeistenBefehle } | null;
  /** Skelett zeigen (Editor noch nicht eingehängt oder Inhalt wird geladen). */
  laedt?: boolean;
  /** Nur lesen: keine Leiste, kein Speicherstand. */
  readOnly?: boolean;
  /** Text im leeren Editor. */
  platzhalter?: string;
  speicherstand?: EditorSpeicherstand;
  /** Konflikt-Hinweis mit den zwei Knöpfen; erscheint nur bei `speicherstand.art === "konflikt"`. */
  konflikt?: EditorKonflikt;
  /** Satz bei Fehlschlag Speichern (additiv, P5-b): Standard der Satz des Protokolltexts; die Seite nennt den Gegenstand beim Namen („Notiz“). */
  fehlschlagSatz?: string;
  /** Zugänglicher Name der Leiste. */
  label?: string;
  /**
   * Menü „Einfügen“ neben der Leiste (Wiki-Umfang, Paket E-W2, additiv): Befehle aus `useHornEinfuegen`. Ohne die Angabe bleibt der
   * Rahmen unverändert (Besprechungs-Protokoll, Serienformular, Vorlagen-Editor).
   */
  einfuegen?: EditorEinfuegeBefehle | null;
  /**
   * Höhe des Skeletts, passend zur Fläche des Editors (`editorHuelleOptionen(label, hoehe)`, additiv, Abnahme 19.10.2026): `standard` (Mindesthöhe 14 rem),
   * `kompakt` (6 rem) oder `mitwachsend` (keine Mindesthöhe); so springt das Layout beim Einhängen nicht.
   */
  hoehe?: EditorFlaechenHoehe;
  /** Die Inhaltsfläche des Editors. */
  children: ReactNode;
}) {
  if (laedt || !leiste) {
    return (
      <>
        {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-text-editors/rich-text-editor-card */}
        <div className={readOnly && rahmenlos ? RAHMEN_LESEN_OHNE_KLASSE : RAHMEN_KLASSE} aria-busy="true">
          {!readOnly && (
            <div className={SKELETT_KOPF_KLASSE}>
              <div className={SKELETT_KNOPF_KLASSE} />
              <div className={SKELETT_KNOPF_KLASSE} />
              <div className={SKELETT_KNOPF_KLASSE} />
              <div className={SKELETT_KNOPF_KLASSE} />
              <div className={SKELETT_KNOPF_KLASSE} />
            </div>
          )}
          <div className={hoehe === "kompakt" ? SKELETT_FLAECHE_KOMPAKT_KLASSE : hoehe === "mitwachsend" ? SKELETT_FLAECHE_MITWACHSEND_KLASSE : hoehe === "buendig" ? SKELETT_FLAECHE_BUENDIG_KLASSE : SKELETT_FLAECHE_KLASSE}>
            <div className={SKELETT_ZEILE_KURZ_KLASSE} />
            {hoehe !== "mitwachsend" && hoehe !== "buendig" && <div className={SKELETT_ZEILE_KLASSE} />}
            {hoehe === "standard" && <div className={SKELETT_ZEILE_KLASSE} />}
            {hoehe === "standard" && <div className={SKELETT_ZEILE_KURZ_KLASSE} />}
          </div>
          <span className="sr-only" role="status">
            Editor lädt
          </span>
        </div>
      </>
    );
  }

  const bearbeitbar = !readOnly && leiste.zustand.bearbeitbar;
  const stand: EditorSpeicherstand = speicherstand ?? { art: "unbekannt" };

  return (
    <>
      {/* Preline: forms/forms-sub-core-inputs-layouts/forms-cat-text-editors/rich-text-editor-card */}
      <div className={bearbeitbar ? RAHMEN_KLASSE : readOnly && rahmenlos ? RAHMEN_LESEN_OHNE_KLASSE : RAHMEN_LESEN_KLASSE}>
        {bearbeitbar && (
          <div className={KOPF_KLASSE}>
            {einfuegen ? (
              <div className={LEISTE_MIT_MENUE_KLASSE}>
                <EditorLeiste zustand={leiste.zustand} befehle={leiste.befehle} label={label} />
                <div className={MENUE_KLASSE}>
                  <EditorEinfuegenMenue befehle={einfuegen} />
                </div>
              </div>
            ) : (
              <EditorLeiste zustand={leiste.zustand} befehle={leiste.befehle} label={label} />
            )}
            <div className={STAND_KOPF_KLASSE}>
              <EditorSpeicherstandAnzeige stand={stand} />
            </div>
          </div>
        )}
        {bearbeitbar && stand.art === "konflikt" && konflikt && (
          <div className={KONFLIKT_KLASSE}>
            <Alert ton="warnung">{konflikt.satz}</Alert>
            <div className={KONFLIKT_KNOEPFE_KLASSE}>
              {/* Preline: component/buttons/buttons-types */}
              <Knopf variante="sekundaer" onClick={konflikt.beiNeuere}>
                {konflikt.neuereText}
              </Knopf>
              <Knopf variante="gefahr" onClick={konflikt.beiMeine}>
                {konflikt.meineText}
              </Knopf>
              {konflikt.unterschiede && (
                <Knopf variante="text" onClick={konflikt.unterschiede.onClick}>
                  {konflikt.unterschiede.text}
                </Knopf>
              )}
            </div>
          </div>
        )}
        <div className={FLAECHE_KLASSE}>
          {bearbeitbar && leiste.zustand.leer && platzhalter && (
            <p className={PLATZHALTER_KLASSE} aria-hidden="true">
              {platzhalter}
            </p>
          )}
          {children}
        </div>
      </div>
      {bearbeitbar && stand.art === "fehlgeschlagen" && (
        <div className="mt-2">
          <Hinweissatz>{fehlschlagSatz}</Hinweissatz>
        </div>
      )}
      {bearbeitbar && (
        <div className={STAND_UNTEN_KLASSE}>
          <EditorSpeicherstandAnzeige stand={stand} />
        </div>
      )}
    </>
  );
}
